import React, { useState } from 'react'
import { Plus, Repeat } from 'lucide-react'
import { PlacePicker } from '../../places/PlacePicker'
import { RecurrencePicker, type RecurrenceRule } from '../../recurring/RecurrencePicker'
import { TaskService, type Task, type UpdateTaskRequest } from '../../../services/taskService'
import { DEADLINE_TYPE_OPTIONS, titleHint, stakesWords, recurrenceWords } from '../../../utils/taskForm'
import { InlinePopover, ChipRow } from './InlinePopover'

/**
 * Pass two of edit-in-place (owner 2026-09-05): the words — title, why,
 * done-means, first step, stakes, where, repeats — each edited where it is
 * shown. With these, the separate edit page has nothing left to offer a
 * task, and the Edit button leaves the page.
 */
interface EditorProps {
  task: Task
  onChanged: (task: Task) => void
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
  /** Bump to open programmatically (the gaps card's "rename it"). */
  openRequest?: number
}

async function write(task: Task, payload: UpdateTaskRequest, onChanged: (t: Task) => void) {
  const svc = new TaskService()
  await svc.updateTask(task.id, payload)
  onChanged(await svc.getTask(task.id))
}

const inputClass = 'w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'

const HOW_MUCH = [
  { value: 'low', label: 'A little' },
  { value: 'medium', label: 'Some' },
  { value: 'high', label: 'A lot' },
]

// ── Title ──────────────────────────────────────────────────────────────────

export function TitleEditor({ task, onChanged, trigger, variant = 'dotted', className, openRequest }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(task.title || '')
  const save = async () => {
    const text = value.trim()
    if (!text) throw new Error('A task needs a name.')
    if (text === (task.title || '').trim()) return
    await write(task, { title: text }, onChanged)
  }
  const hint = titleHint(value)
  return (
    <InlinePopover label="Title" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-title" width="w-96" openRequest={openRequest}
                   trigger={trigger ?? task.title}>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Title"
        autoFocus
        className={inputClass}
        placeholder="Say it as something to do — Call…, Write…, Book…"
      />
      <p className={`text-app-micro ${hint ? 'text-warn' : 'text-muted'}`}>{hint || 'Starts with a verb, names the outcome.'}</p>
    </InlinePopover>
  )
}

// ── One-line text facts: why, done means, first step ───────────────────────

function TextFactEditor({ task, onChanged, trigger, variant = 'dotted', className, field, label, placeholder, current, extra }: EditorProps & {
  field: 'motivation' | 'acceptance_criteria' | 'minimum_starter_step'
  label: string
  placeholder: string
  current: string | null | undefined
  extra?: (draft: Record<string, string>, set: (k: string, v: string) => void) => React.ReactNode
  }) {
  const [text, setText] = useState('')
  const [draft, setDraft] = useState<Record<string, string>>({})
  const reset = () => { setText(current || ''); setDraft({ level: task.motivation_level || '' }) }
  const save = async () => {
    const t = text.trim()
    const payload: UpdateTaskRequest = { [field]: t || null } as UpdateTaskRequest
    if (field === 'motivation') payload.motivation_level = (draft.level || null) as UpdateTaskRequest['motivation_level']
    await write(task, payload, onChanged)
  }
  return (
    <InlinePopover label={label} variant={variant} className={className} onOpen={reset} onSave={save} testId={`task-edit-${field}`} width="w-96"
                   trigger={trigger ?? (current || <><Plus className="h-3 w-3" /> {label.toLowerCase()}</>)}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        aria-label={label}
        autoFocus
        placeholder={placeholder}
        className={`${inputClass} resize-y`}
      />
      {extra?.(draft, (k, v) => setDraft((d) => ({ ...d, [k]: v })))}
      {current && <button type="button" onClick={() => setText('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Clear it</button>}
    </InlinePopover>
  )
}

export function WhyEditor(p: EditorProps) {
  return (
    <TextFactEditor {...p} field="motivation" label="Why it matters" current={p.task.motivation}
                    placeholder="In your words — one line is plenty"
                    extra={(draft, set) => (
                      <div>
                        <div className="mb-1 text-app-micro text-muted">How much, right now</div>
                        <ChipRow label="How much" options={HOW_MUCH} value={draft.level || ''} onChange={(v) => set('level', v)} />
                      </div>
                    )} />
  )
}

export function DoneMeansEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="acceptance_criteria" label="Done means" current={p.task.acceptance_criteria}
                         placeholder="e.g. the form is filed and the receipt is in the drawer" />
}

export function FirstStepEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="minimum_starter_step" label="First step" current={p.task.minimum_starter_step}
                         placeholder="The first five minutes" />
}

// ── Stakes: how firm the date is, and what slips ───────────────────────────

export function StakesEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [type, setType] = useState('')
  const [consequence, setConsequence] = useState('')
  const reset = () => { setType(task.deadline_type || ''); setConsequence(task.deadline_consequence || '') }
  const save = async () => {
    if (!type && !consequence.trim()) throw new Error('Say how firm the time is, or what changes if it passes.')
    await write(task, { deadline_type: (type || null) as UpdateTaskRequest['deadline_type'], deadline_consequence: consequence.trim() || null }, onChanged)
  }
  return (
    <InlinePopover label="If the time passes" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-stakes" width="w-96"
                   trigger={trigger ?? (stakesWords(task) || <><Plus className="h-3 w-3" /> if the time passes</>)}>
      <ChipRow label="How firm is the date" options={DEADLINE_TYPE_OPTIONS} value={type} onChange={setType} />
      <input type="text" value={consequence} onChange={(e) => setConsequence(e.target.value)} placeholder="What changes if the time passes…" aria-label="If the time passes" className={inputClass} />
    </InlinePopover>
  )
}

// ── Where: a saved place, or words ─────────────────────────────────────────

export function WhereEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [placeId, setPlaceId] = useState<number | null>(null)
  const [location, setLocation] = useState('')
  const reset = () => { setPlaceId(task.place_id ?? null); setLocation(task.location || '') }
  const save = async () => {
    await write(task, { place_id: placeId, location: location.trim() || null }, onChanged)
  }
  const words = task.location || (task.place_id ? 'A saved place' : null)
  return (
    <InlinePopover label="Where" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-where" width="w-96"
                   trigger={trigger ?? (words || <><Plus className="h-3 w-3" /> where</>)}>
      <PlacePicker value={placeId} onChange={setPlaceId} />
      <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Or describe it — the garage, the bank on 5th" aria-label="Where, in words" className={inputClass} />
    </InlinePopover>
  )
}

// ── Repeats ────────────────────────────────────────────────────────────────

export function RepeatsEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [rule, setRule] = useState<RecurrenceRule | null>(null)
  const reset = () => setRule(task.is_recurring && task.recurrence_rule && typeof task.recurrence_rule === 'object' ? (task.recurrence_rule as RecurrenceRule) : null)
  const save = async () => {
    await write(task, { is_recurring: Boolean(rule), recurrence_rule: rule, recurrence_pattern: rule?.frequency || null } as UpdateTaskRequest, onChanged)
  }
  const words = recurrenceWords(task)
  return (
    <InlinePopover label="Repeats" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-repeats" width="w-96"
                   trigger={trigger ?? (words ? <><Repeat className="mr-1 inline h-3.5 w-3.5" />{words}</> : <><Plus className="h-3 w-3" /> repeats</>)}>
      <RecurrencePicker value={rule} onChange={setRule} />
      {task.is_recurring && <button type="button" onClick={() => setRule(null)} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Stop repeating</button>}
    </InlinePopover>
  )
}
